import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../lib/api';
import { useReport } from '../reporting/ReportContext';
import { FilterBar } from '../reporting/FilterBar';
import { PageHead } from '../reporting/PageHead';
import { KpiCard, Panel } from '../dashboard/ui';
import { SimpleBars } from '../dashboard/charts';
import { CHANNEL_COLORS } from '../dashboard/colors';
import { downloadCsv, filtersToQuery, metres, niceDate, num, pct, type MilestoneDistanceReport } from '../lib/reporting';

const PRIMARY = CHANNEL_COLORS.NVS;
const THRESHOLDS = [100, 250, 500, 1000];

export function MilestoneDistance() {
  // Shared NVS filter bar (date / region / store / driver) plus a page-local
  // "far" threshold, read from /api/reports/milestone-distance.
  const { filters } = useReport();
  const [threshold, setThreshold] = useState(250);
  const [data, setData] = useState<MilestoneDistanceReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!filters.from || !filters.to) return;
    let active = true;
    setError(null);
    apiFetch<MilestoneDistanceReport>(`/api/reports/milestone-distance?${filtersToQuery(filters)}&threshold=${threshold}`)
      .then((d) => {
        if (active) setData(d);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof ApiError ? err.message : 'Failed to load milestone distance report');
      });
    return () => {
      active = false;
    };
  }, [filters, threshold]);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(
      `nvs-milestone-distance-${filters.from}_${filters.to}.csv`,
      data.flaggedList.map((r) => ({
        date: r.date ?? '',
        stop_ref: r.stopRef ?? '',
        region: r.region ?? '',
        driver: r.driver ?? '',
        address: r.address ?? '',
        arrived_distance_m: r.arrivedM ?? '',
        completed_distance_m: r.completedM ?? '',
      })),
    );
  };

  const t = data?.totals;
  const listCapped = data?.flaggedList.length === 500;

  return (
    <section className="report">
      <PageHead
        crumb="Reporting / Milestone location"
        title="Milestone location check"
        subtitle={
          data
            ? `NVS in-house · ${data.from} → ${data.to} · distance of the driver's Arrived & Completed milestones from the delivery point. DPD handoffs excluded.`
            : "NVS in-house · distance of the driver's Arrived & Completed milestones from the delivery point"
        }
      />
      <FilterBar onExport={exportCsv} />

      <div className="filter-presets threshold-bar" role="group" aria-label="Flag threshold">
        <span className="filter-label">Flag when further than</span>
        {THRESHOLDS.map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={threshold === m}
            className={threshold === m ? 'is-active' : undefined}
            onClick={() => setThreshold(m)}
          >
            {metres(m)}
          </button>
        ))}
      </div>

      {error && <p className="form-error" role="alert">{error}</p>}
      {!data && !error && <p className="muted">Loading…</p>}

      {data && t && t.completedStops === 0 && (
        <div className="empty-note">
          <strong>No completed stops in this range.</strong>
          <p className="muted">The location check runs on completed deliveries only.</p>
        </div>
      )}

      {data && t && t.completedStops > 0 && t.checked === 0 && (
        <div className="empty-note">
          <strong>No milestone GPS positions synced for this range yet.</strong>
          <p className="muted">
            {num(t.completedStops)} completed stops are in range, but none have the driver&apos;s device position
            for the Arrived / Completed milestones alongside the delivery point&apos;s coordinates. Once the Locate2u
            sync writes them to <code>stop_milestone_locations</code>, this report fills in automatically.
          </p>
        </div>
      )}

      {data && t && t.checked > 0 && (
        <>
          <div className="kpi-grid">
            <KpiCard
              label="Stops flagged"
              value={num(t.flagged)}
              sub={`${pct(t.pctFlagged)} of ${num(t.checked)} checked · > ${metres(data.thresholdM)}`}
              tone={t.flagged === 0 ? 'good' : 'bad'}
              accent="var(--danger)"
            />
            <KpiCard
              label="Arrived far from drop"
              value={num(t.arrivedFar)}
              sub={`Median ${metres(t.medianArrivedM)} · ${num(t.arrivedChecked)} with GPS`}
              accent={PRIMARY}
            />
            <KpiCard
              label="Completed far from drop"
              value={num(t.completedFar)}
              sub={`Median ${metres(t.medianCompletedM)} · 90th pct ${metres(t.p90CompletedM)}`}
              accent={PRIMARY}
            />
            <KpiCard
              label="GPS coverage"
              value={pct(t.checked / t.completedStops)}
              sub={`${num(t.checked)} of ${num(t.completedStops)} completed stops`}
              accent="var(--border)"
            />
          </div>

          <Panel title="Completed milestone distance from delivery point" subtitle="Completed stops by distance band">
            <SimpleBars
              data={data.buckets.map((b) => ({ label: b.bucket, value: b.stops }))}
              format={(v) => num(v)}
              color={PRIMARY}
            />
          </Panel>

          <Panel title="By driver" subtitle={`Drivers with flagged stops first (top 50) · flag > ${metres(data.thresholdM)}`}>
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th className="num">Checked</th>
                    <th className="num">Flagged</th>
                    <th className="num">% flagged</th>
                    <th className="num">Median (completed)</th>
                    <th className="num">Furthest</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byDriver.map((d) => (
                    <tr key={d.driver}>
                      <td>{d.driver}</td>
                      <td className="num">{num(d.checked)}</td>
                      <td className="num">{num(d.flagged)}</td>
                      <td className="num">{pct(d.pctFlagged)}</td>
                      <td className="num">{metres(d.medianCompletedM)}</td>
                      <td className="num">{metres(d.maxM)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="By region" subtitle="Checked stops per team region">
            <div className="data-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Region</th>
                    <th className="num">Checked</th>
                    <th className="num">Flagged</th>
                    <th className="num">% flagged</th>
                    <th className="num">Median (completed)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.byRegion.map((r) => (
                    <tr key={r.region}>
                      <td>{r.region}</td>
                      <td className="num">{num(r.checked)}</td>
                      <td className="num">{num(r.flagged)}</td>
                      <td className="num">{pct(r.pctFlagged)}</td>
                      <td className="num">{metres(r.medianCompletedM)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel
            title="Flagged stops"
            subtitle={
              listCapped
                ? 'Showing the 500 furthest stops in range — narrow the filters to see the rest'
                : `${num(data.flaggedList.length)} stop(s) with a milestone further than ${metres(data.thresholdM)} from the drop`
            }
          >
            {data.flaggedList.length === 0 ? (
              <div className="empty-note">
                <strong>No milestones beyond {metres(data.thresholdM)} in this range.</strong>
                <p className="muted">Every checked Arrived / Completed milestone was recorded near the delivery point.</p>
              </div>
            ) : (
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Stop ref</th>
                      <th>Driver</th>
                      <th>Address</th>
                      <th className="num">Arrived</th>
                      <th className="num">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.flaggedList.map((r, i) => (
                      <tr key={`${r.stopRef}-${i}`}>
                        <td>{r.date ? niceDate(r.date) : '—'}</td>
                        <td>{r.stopRef ?? '—'}</td>
                        <td>{r.driver ?? '—'}</td>
                        <td className="muted">{r.address ?? '—'}</td>
                        <td className={`num${r.arrivedM != null && r.arrivedM > data.thresholdM ? ' is-bad' : ''}`}>{metres(r.arrivedM)}</td>
                        <td className={`num${r.completedM != null && r.completedM > data.thresholdM ? ' is-bad' : ''}`}>{metres(r.completedM)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </section>
  );
}
